import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { httpGet, httpGetFile, httpPost, PAGESIZE } from '../../helpers/http.js';
import { sortOnDate } from '../../helpers/controls.js';
import { deepExtend } from '../../helpers/extend.js';

// The filters of the overview as query parameters, for the list and the export.
const toFilterParams = ({ search, sort, showExpired, organization }, params = new URLSearchParams()) => {
    if (search) {
        params.set('search', search);
    }
    if (sort) {
        params.set('sort', sort);
    }
    params.set('showExpired', showExpired);
    if (organization) {
        params.set('organization', organization);
    }
    return params;
};

export const loadAccreditationInfosIfNeeded = createAsyncThunk(
    'accreditationInfo/fetchList',
    async (args, { getState }) => {
        const { accreditationInfo: state, env, auth } = getState();

        const newPage = args.reload ? 0 : state.page + 1;

        const search = args.search || args.search === '' ? args.search : state.search;
        const sort = args.sort || state.sort;
        const showExpired = args.showExpired ?? state.showExpired;
        const organization = args.organization ?? state.organization;

        const params = toFilterParams({ search, sort, showExpired, organization }, new URLSearchParams({
            pageIndex: newPage,
            pageSize: PAGESIZE,
        }));

        const response = await httpGet(`${env.settings.learningApi}/api/1.0/accreditationinfo?${params}`, auth.user);

        return {
            list: response.results,
            page: newPage,
            total: response.count,
            reload: args.reload,
            search,
            sort,
            showExpired,
            organization,
        };
    },
    {
        condition: (args, { getState }) => {
            const { accreditationInfo: state } = getState();

            if (!args.reload && (args.index < state.list.length - 1 || args.index >= state.total - 1)) {
                return false;
            }
        }
    },
);

// Every mapping of the settings of the current filters as Excel, built by the API: the list only holds
// the loaded pages.
export const exportAccreditationInfos = createAsyncThunk(
    'accreditationInfo/export',
    async (args, { getState }) => {
        const { accreditationInfo: state, env, auth } = getState();

        await httpGetFile(
            `${env.settings.learningApi}/api/1.0/accreditationinfo/export?${toFilterParams(state)}`,
            auth.user,
            'accreditatie-instellingen.xlsx');
    },
);

// All professional organisations, for the filters of the accreditation overviews.
export const loadOrganizationOptionsIfNeeded = createAsyncThunk(
    'accreditationInfo/fetchOrganizationOptions',
    async (args, { getState }) => {
        const { env, auth } = getState();

        const response = await httpGet(`${env.settings.learningApi}/api/1.0/accreditationinfo/organizations`, auth.user);

        return response
            .map((o) => ({ value: o.key, label: o.key })) // the key is the short name, as on the settings screen
            .sort((a, b) => a.label.localeCompare(b.label));
    },
    {
        condition: (args, { getState }) => getState().accreditationInfo.organizationOptions.length === 0,
    },
);

export const loadAccreditationInfo = createAsyncThunk(
    'accreditationInfo/fetch',
    async (args, { getState }) => {
        const { targetId } = args;
        const { env, auth } = getState();

        const response = await httpGet(`${env.settings.learningApi}/api/1.0/accreditationinfo/${targetId}`, auth.user);

        return {
            catalogItemName: response.name,
            accreditations: response.accreditations || [],
            organizations: response.organizations.map((o) => ({
                value: o.key,
                label: o.key,
            })),
        };
    },
);

export const saveAccreditationInfo = createAsyncThunk(
    'accreditationInfo/save',
    async (args, { getState }) => {
        const { targetId } = args;
        const { accreditationInfo: state, env, auth } = getState();

        // $opened and $new are UI state, not part of the settings.
        const accreditations = state.current.map(({ $opened, $new, ...mapping }) => mapping);

        return await httpPost(`${env.settings.learningApi}/api/1.0/accreditationinfo/${targetId}`, auth.user, accreditations);
    },
);

const accreditationInfoSlice = createSlice({
    name: 'accreditationInfo',
    initialState: {
        status: 'idle',
        error: null,
        list: [],
        page: -1,
        total: undefined,
        search: undefined,
        sort: 'Expiration',
        showExpired: false,
        organization: '',
        listStatus: 'idle',
        organizationOptions: [],
        exportStatus: 'idle',
        exportError: null,
    },
    reducers: {
        updateAccreditationMapping: (state, action) => {
            const { index, changes } = action.payload;

            state.current = state.current.map((o, i) => i === index ? deepExtend(o, changes) : o);
            state.dirty = true;
        },
        createAccreditationMapping: (state) => {
            state.current.unshift({
                supplierKey: 'TCG',
                organizationKey: state.organizations[0].value,
                data: {},
                $opened: true,
                $new: true,
            });
            state.dirty = true;
        },
        removeAccreditationMapping: (state, action) => {
            const { index } = action.payload;

            state.current.splice(index, 1);
            state.dirty = true;
        },
        openAccreditationMapping: (state, action) => {
            const { index, value } = action.payload;

            state.current[index].$opened = value;
        },
        // After a save attempt the mappings added so far are no longer new, so they are marked when invalid.
        markAccreditationSaveAttempt: (state) => {
            state.current.forEach((o) => { o.$new = false; });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadAccreditationInfosIfNeeded.pending, (state) => {
                state.listStatus = 'loading';
            })
            .addCase(loadAccreditationInfosIfNeeded.fulfilled, (state, action) => {
                const { payload } = action;

                state.listStatus = 'success';
                state.list = payload.reload ? [...payload.list] : [...state.list, ...payload.list];
                state.page = payload.page;
                state.total = payload.total;
                state.search = payload.search;
                state.sort = payload.sort;
                state.showExpired = payload.showExpired;
                state.organization = payload.organization;
            })
            .addCase(loadOrganizationOptionsIfNeeded.fulfilled, (state, action) => {
                state.organizationOptions = action.payload;
            })
            .addCase(exportAccreditationInfos.pending, (state) => {
                state.exportStatus = 'loading';
                state.exportError = null;
            })
            .addCase(exportAccreditationInfos.fulfilled, (state) => {
                state.exportStatus = 'idle';
            })
            .addCase(exportAccreditationInfos.rejected, (state, action) => {
                state.exportStatus = 'failed';
                state.exportError = action.error.message;
            })
            .addCase(loadAccreditationInfosIfNeeded.rejected, (state, action) => {
                state.listStatus = 'failed';
                state.error = action.error;
            })
            .addCase(loadAccreditationInfo.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadAccreditationInfo.fulfilled, (state, action) => {
                const { catalogItemName, accreditations, organizations } = action.payload;

                state.status = 'success';
                state.catalogItemName = catalogItemName;
                state.current = sortOnDate(accreditations, 'validFrom', true);
                state.organizations = organizations;
                state.dirty = false;
            })
            .addCase(loadAccreditationInfo.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(saveAccreditationInfo.pending, (state) => {
                state.status = 'saving';
            })
            .addCase(saveAccreditationInfo.fulfilled, (state) => {
                state.status = 'success';
                state.current = sortOnDate(state.current.map((o) => ({ ...o, $opened: false, $new: false })), 'validFrom', true);
                state.dirty = false;
            })
            .addCase(saveAccreditationInfo.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            });
    },
});

export const {
    updateAccreditationMapping,
    createAccreditationMapping,
    removeAccreditationMapping,
    openAccreditationMapping,
    markAccreditationSaveAttempt,
} = accreditationInfoSlice.actions;

export default accreditationInfoSlice.reducer;
